/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { OSearchResultsComponent } from '../OSearchResults';
import { searchResultMock } from '../../mocks/resultmock';
import { MiniSearchBar } from '../MiniSearchBar';
import { NLSSearchTriggerParams } from '../MiniSearchBar/MiniSearchBar.types';
import { useNLSSearch } from '../../hooks/useNLSSearch';
import { buildSearchQueryDataFromStore, findLatestBaseQueryFromMessages } from '../../utils/ampRequestMapper';
import { useSearchFormStore } from '../../store/searchFormStore';
import { useMarriottAIPreviewStore, ConversationFilterState } from '../../store';
import { SPECIAL_RATES, USE_POINTS } from '../../store/store.constants';
import {
  SearchBarStrip,
  PillWrapper,
  FloatingLoadingPill,
  Dot,
  ResultsRelativeWrapper,
} from './HotelResultsView.styles';
import { HotelResultsViewProps } from './HotelResultsView.types';
import { NLSSelectedFacetMap } from '../../services/nlsSearch.types';
import { buildSelectedFacetsFromNlpEntities } from '../../services/nlsSearchService';

/**
 * Converts NLSSelectedFacetMap to ConversationFilterState format.
 * Maps the shop's facet keys to the conversation store's filter state structure.
 */
const convertFacetsToFilterState = (facets: NLSSelectedFacetMap): Partial<ConversationFilterState> => {
  const filterState: Partial<ConversationFilterState> = {};

  // AMENITIES -> amenities (array of codes)
  if (facets['AMENITIES'] && Object.keys(facets['AMENITIES']).length > 0) {
    filterState.amenities = Object.keys(facets['AMENITIES']);
  }

  // BRANDS -> brands (array of codes)
  if (facets['BRANDS'] && Object.keys(facets['BRANDS']).length > 0) {
    filterState.brands = Object.keys(facets['BRANDS']);
  }

  // PROPERTY_TYPES -> hotelTypes (array of codes)
  if (facets['PROPERTY_TYPES'] && Object.keys(facets['PROPERTY_TYPES']).length > 0) {
    filterState.hotelTypes = Object.keys(facets['PROPERTY_TYPES']);
  }

  // ACTIVITIES -> activities (array of codes)
  if (facets['ACTIVITIES'] && Object.keys(facets['ACTIVITIES']).length > 0) {
    filterState.activities = Object.keys(facets['ACTIVITIES']);
  }

  // TRANSPORTATION_TYPES -> transportationTypes (array of codes)
  if (facets['TRANSPORTATION_TYPES'] && Object.keys(facets['TRANSPORTATION_TYPES']).length > 0) {
    filterState.transportationTypes = Object.keys(facets['TRANSPORTATION_TYPES']);
  }

  // PRICE -> priceRange (min/max)
  // Shop uses format like { "1-PRICE": "100-200" } where value is "start-end"
  if (facets['PRICE'] && Object.keys(facets['PRICE']).length > 0) {
    const priceEntries = Object.entries(facets['PRICE']);
    let minPrice: number | undefined;
    let maxPrice: number | undefined;

    for (const [_key, value] of priceEntries) {
      // Try to parse the value as "start-end" format (e.g., "100-200")
      if (typeof value === 'string' && value.includes('-')) {
        const [start, end] = value.split('-').map(v => {
          const num = parseInt(v, 10);
          return isNaN(num) ? undefined : num;
        });
        if (start !== undefined) {
          minPrice = minPrice === undefined ? start : Math.min(minPrice, start);
        }
        if (end !== undefined && end !== 9999) {
          // 9999 or 'overflow' means no max
          maxPrice = maxPrice === undefined ? end : Math.max(maxPrice, end);
        }
      } else {
        // Fallback: try to parse the key or value as a number
        const numValue = typeof value === 'number' ? value : parseInt(String(value), 10);
        if (!isNaN(numValue)) {
          minPrice = minPrice === undefined ? numValue : Math.min(minPrice, numValue);
          maxPrice = maxPrice === undefined ? numValue : Math.max(maxPrice, numValue);
        }
      }
    }

    if (minPrice !== undefined || maxPrice !== undefined) {
      filterState.priceRange = {
        min: minPrice,
        max: maxPrice,
      };
    }
  }

  // POINTS -> pointsRange (min/max)
  // Shop uses format like { "1-POINT": "0-40000" } where value is "start-end"
  if (facets['POINTS'] && Object.keys(facets['POINTS']).length > 0) {
    const pointsEntries = Object.entries(facets['POINTS']);
    let minPoints: number | undefined;
    let maxPoints: number | undefined;

    for (const [_key, value] of pointsEntries) {
      // Try to parse the value as "start-end" format (e.g., "0-40000")
      if (typeof value === 'string' && value.includes('-')) {
        const [start, end] = value.split('-').map(v => {
          const num = parseInt(v, 10);
          return isNaN(num) ? undefined : num;
        });
        if (start !== undefined) {
          minPoints = minPoints === undefined ? start : Math.min(minPoints, start);
        }
        if (end !== undefined && end !== 999999) {
          // 999999 or 'overflow' means no max
          maxPoints = maxPoints === undefined ? end : Math.max(maxPoints, end);
        }
      } else {
        // Fallback: try to parse the key or value as a number
        const numValue = typeof value === 'number' ? value : parseInt(String(value), 10);
        if (!isNaN(numValue)) {
          minPoints = minPoints === undefined ? numValue : Math.min(minPoints, numValue);
          maxPoints = maxPoints === undefined ? numValue : Math.max(maxPoints, numValue);
        }
      }
    }

    if (minPoints !== undefined || maxPoints !== undefined) {
      filterState.pointsRange = {
        min: minPoints,
        max: maxPoints,
      };
    }
  }

  // DISTANCE -> distance
  // Shop uses format like { "1-DIST": "0-4830" } where value is "start-end" in meters
  if (facets['DISTANCE'] && Object.keys(facets['DISTANCE']).length > 0) {
    const distanceEntries = Object.entries(facets['DISTANCE']);
    let maxDistance: number | undefined;

    for (const [_key, value] of distanceEntries) {
      // Try to parse the value as "start-end" format (e.g., "0-4830")
      if (typeof value === 'string' && value.includes('-')) {
        const parts = value.split('-');
        const end = parseInt(parts[1], 10);
        if (!isNaN(end)) {
          maxDistance = maxDistance === undefined ? end : Math.max(maxDistance, end);
        }
      } else {
        // Fallback: try to parse the key or value as a number
        const numValue = typeof value === 'number' ? value : parseInt(String(value), 10);
        if (!isNaN(numValue)) {
          maxDistance = maxDistance === undefined ? numValue : Math.max(maxDistance, numValue);
        }
      }
    }

    if (maxDistance !== undefined) {
      filterState.distance = maxDistance;
    }
  }

  // MEETINGS_EVENTS -> events (array of codes)
  if (facets['MEETINGS_EVENTS'] && Object.keys(facets['MEETINGS_EVENTS']).length > 0) {
    filterState.events = Object.keys(facets['MEETINGS_EVENTS']);
  }

  // STATES -> states (array of codes)
  if (facets['STATES'] && Object.keys(facets['STATES']).length > 0) {
    filterState.states = Object.keys(facets['STATES']);
  }

  // CITIES -> cities (array of codes)
  if (facets['CITIES'] && Object.keys(facets['CITIES']).length > 0) {
    filterState.cities = Object.keys(facets['CITIES']);
  }

  return filterState;
};

export const HotelResultsView: React.FC<HotelResultsViewProps> = ({
  viewData: viewDataProp,
  onOpenHotelPanel,
  onPropertyMediaLoaded,
  isStaticMap = false,
  isMapLoading = false,
  uiData,
  isMapView = false,
  miniSearchBarModel,
  isMiniSearchLoading = false,
  llmMetadata,
  onNLSSearchComplete,
}) => {
  const _hasQueryRewrite = Boolean(llmMetadata?.payload?.query || llmMetadata?.llmInfo?.queryRewrite);

  // Subscribe directly to the preview store so viewData updates immediately
  // when executeSearch calls updateViewData — without waiting for the
  // OChat → PreviewPanel prop re-render cycle.
  const storeViewData = useMarriottAIPreviewStore((s: any) => s.currentState?.data);
  // Use the store value when available (MiniSearchBar flow); fall back to the
  // prop value for the initial TIP-driven render.
  const viewData = storeViewData ?? viewDataProp;
  const currentViewStateId = useMarriottAIPreviewStore((s: any) => s.currentState?.id);

  // Check if this is a single property response (e.g., availability_calendar_search)
  // Hide MiniSearchBar/filters for single property responses
  const isSinglePropertyResponse = viewData?.isSinglePropertyResponse === true;

  // Get store actions for syncing filter state to conversation
  const currentChatId = useMarriottAIPreviewStore((s: any) => s.currentChatId);
  const setConversationFilterState = useMarriottAIPreviewStore((s: any) => s.setConversationFilterState);

  // Current chat messages — used to recover the NLS base query when the active
  // response is a single-property availability_calendar_search (which carries no
  // NLS query of its own).
  const chatMessages = useMarriottAIPreviewStore((s: any) => s.messages);

  const {
    executeSearch,
    onDatesChange,
    onRoomsChange,
    onRatesChange,
    onFacetClick,
    onApplyFilters,
    setBaseQuery,
    syncSelectedFacets,
    isLoading: isNLSLoading,
  } = useNLSSearch({
    onSearchComplete: result => {
      onNLSSearchComplete?.(result);
    },
  });

  // Holds the latest facets for the filter modal.
  // Seeded from the initial TIP/NLS facets immediately so the modal shows correct
  // counts on first open. Overwritten by each NLS filter-refresh call.
  // Shape must match what FiltersComponent expects: { facets: NLSFacet[] }
  const [aiFilterUpdateData, setAiFilterUpdateData] = useState<{ facets: any[] } | undefined>(undefined);

  // Derive available facets from the TIP/NLS search response.
  const nlsResult = viewData?.rawData?.data?.search?.conversationalSearch?.searchByNaturalLanguageQuery;

  // ── NLP-entity pre-selected facets ───────────────────────────────────────
  // Derived synchronously from viewData so SearchResultsComponent always
  // receives the correct value on its initial render (including after a key-
  // driven remount when switching between messages). Using useEffect/state
  // for this caused a render-order race: the shop would remount with the
  // stale prop before the effect fired to clear it.
  const nlpEntities = nlsResult?.nlpEntities;
  const nlpPreSelectedFacets = useMemo(() => {
    if (!nlpEntities) return undefined;
    const preSelected = buildSelectedFacetsFromNlpEntities(nlpEntities);
    return Object.keys(preSelected).length > 0 ? preSelected : undefined;
  }, [nlpEntities]);

  // ── User-applied facets ───────────────────────────────────────────────────
  // Captured when user clicks "Apply" in the filter modal or triggers a
  // MiniSearchBar search. Cleared when viewData changes (new message or
  // "View All Properties" click) so the fresh NLP pre-selection takes over.
  const [aiAppliedFacets, setAiAppliedFacets] = useState<NLSSelectedFacetMap | undefined>(undefined);

  // ── Unified effect: reset applied facets + filter counts on viewData change ──
  // aiPreSelectedFacets is now derived synchronously (above) so this effect
  // only needs to clear the user-applied state and stale filter counts.
  // Reset filters when viewData changes (new TIP response or "View Properties" click)
  // but NOT when switching conversations (handled by separate effect).
  const viewDataRef = useRef<typeof viewData>(null);
  const lastViewDataChatIdRef = useRef<string | null>(null);
  // Declared here (before the unified effect) so the effect can reset it when
  // viewData changes, allowing the facets effect below to re-seed for the new data.
  const nlsFacetsRef = useRef<any[] | null>(null);
  // Track whether the current search was triggered by user-applied filters.
  // This is used to prevent the viewData effect from overwriting the filter state
  // when the search result comes back.
  const userAppliedFilterSearchRef = useRef(false);

  useEffect(() => {
    if (!viewData || viewData === viewDataRef.current) return;

    // Check if this is a conversation switch (chatId changed) vs viewData change within same conversation
    const isConversationSwitch = currentChatId !== lastViewDataChatIdRef.current;
    const isInitialLoad = lastViewDataChatIdRef.current === null;
    lastViewDataChatIdRef.current = currentChatId;
    viewDataRef.current = viewData;

    // Check if this viewData change is from a user-applied filter search
    const isUserAppliedFilterSearch = userAppliedFilterSearchRef.current;
    userAppliedFilterSearchRef.current = false; // Reset the flag

    // Reset filters when viewData changes within the same conversation
    // This handles: new TIP response, "View Properties" click, new search query
    // Don't reset on conversation switch - that's handled by the conversation switch effect
    // Don't reset on user-applied filter search - the filters were already saved in handleApply
    if (!isConversationSwitch && !isUserAppliedFilterSearch) {
      // Reset user-applied facets — new data context takes over.
      // The NLP pre-selected facets (nlpPreSelectedFacets) will be derived from the new viewData.
      setAiAppliedFacets(undefined);
      pendingFacetsRef.current = {};
      syncSelectedFacets({});
    }

    // Store the NLP-derived filters in the conversation's filterState
    // This ensures "Search All Properties" URL includes the correct filters
    // Do this on:
    // 1. Initial load (first TIP response)
    // 2. ViewData change within same conversation (new TIP, "View Properties" click)
    // 3. Conversation switch (to restore filters from the new conversation's TIP)
    //
    // IMPORTANT: Do NOT overwrite the filter state if this is a user-applied filter search.
    // The user-applied filters were already saved in handleApply and should not be
    // overwritten by the NLP-derived filters from the search result.
    if (currentChatId && setConversationFilterState && !isUserAppliedFilterSearch) {
      if (nlpPreSelectedFacets) {
        const nlpFilterState = convertFacetsToFilterState(nlpPreSelectedFacets);
        setConversationFilterState(currentChatId, {
          brands: nlpFilterState.brands ?? [],
          amenities: nlpFilterState.amenities ?? [],
          priceRange: nlpFilterState.priceRange ?? {},
          pointsRange: nlpFilterState.pointsRange ?? {},
          hotelTypes: nlpFilterState.hotelTypes ?? [],
          activities: nlpFilterState.activities ?? [],
          transportationTypes: nlpFilterState.transportationTypes ?? [],
          events: nlpFilterState.events ?? [],
          distance: nlpFilterState.distance,
          states: nlpFilterState.states ?? [],
          cities: nlpFilterState.cities ?? [],
        });
      } else if (!isConversationSwitch || isInitialLoad) {
        // No NLP filters - clear the filter state only on initial load or same-conversation changes
        // Don't clear on conversation switch as the conversation might have user-applied filters
        setConversationFilterState(currentChatId, {
          brands: [],
          amenities: [],
          priceRange: {},
          pointsRange: {},
          hotelTypes: [],
          activities: [],
          transportationTypes: [],
          events: [],
          distance: undefined,
          states: [],
          cities: [],
        });
      }
    }

    // Clear stale filter counts so the modal doesn't flash old data while the
    // new nlsResult.facets effect re-seeds aiFilterUpdateData below.
    setAiFilterUpdateData(undefined);
    nlsFacetsRef.current = null; // allow facets effect to re-seed for new data
  }, [viewData, currentChatId, syncSelectedFacets, setConversationFilterState, nlpPreSelectedFacets]);

  // ── Track conversation changes for filter state management ──────────────────────
  // This effect tracks when the conversation changes. The actual filter reset is handled
  // by the viewData effect above, which detects conversation switches and clears filters.
  // This effect is kept minimal to avoid race conditions with the viewData effect.
  const prevChatIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (currentChatId && currentChatId !== prevChatIdRef.current) {
      prevChatIdRef.current = currentChatId;
      // Clear filter selections when conversation changes
      // This ensures filters don't persist across conversations
      setAiAppliedFacets(undefined);
      pendingFacetsRef.current = {};
      syncSelectedFacets({});
    }
  }, [currentChatId, syncSelectedFacets]);

  // Whenever the main NLS search result changes (initial TIP load or a new MiniSearchBar search),
  // seed aiFilterUpdateData with the fresh facets from that result.
  // This ensures:
  //   1. On first modal open, all filter categories show correct initial counts.
  //   2. After "Apply" runs a new NLS search, the modal resets to the new result's facets.
  // Individual facet-refresh calls will further overwrite this via setAiFilterUpdateData below.
  useEffect(() => {
    if (nlsResult?.facets && nlsResult.facets !== nlsFacetsRef.current) {
      nlsFacetsRef.current = nlsResult.facets;
      setAiFilterUpdateData({ facets: nlsResult.facets });
    }
  }, [viewData, nlsResult?.facets]);

  // ── Intercept shop filter events and route them through NLS calls ──────────
  //
  // The shop's FiltersComponent (inside SearchResultsComponent) is already rendered
  // and wired up. Instead of replacing its UI, we hook into its event system:
  //
  //  - updateShopPageFilterModalValues: dispatched on every individual filter click
  //    inside the "All Filters" modal. We use this to call the NLS facet-refresh
  //    query (phoenixConversationalSearchByNaturalLanguageFilter, no edges) so that
  //    bucket counts update as the user makes selections.
  //
  //  - callSessionData: dispatched when the user clicks "Apply" in the "All Filters"
  //    modal. We intercept this to fire the full NLS search
  //    (phoenixConversationalSearchByNaturalLanguage, with edges and facets payload).
  //
  // We capture the latest selected facets from the updateShopPageFilterModalValues
  // event in a ref so that callSessionData can read them without stale closures.
  const pendingFacetsRef = useRef<NLSSelectedFacetMap>({});

  // Extract the subset of the shop event payload that maps to NLSSelectedFacetMap.
  // The shop uses keys like AMENITIES, BRANDS, PRICE, DISTANCE — the same keys our
  // NLS facet payload uses.
  // Keys used by shop's allFilters component in its searchQueryState — must match
  // SearchFacetsList values from mi-shop-components/src/constants/Filters.constant.ts
  const SHOP_FACET_KEYS = [
    'AMENITIES',
    'BRANDS',
    'PRICE',
    'POINTS', // Points filter - mutually exclusive with PRICE
    'DISTANCE',
    'PROPERTY_TYPES', // was PROPERTY_TYPE — shop uses PROPERTY_TYPES
    'ACTIVITIES',
    'HOTEL_SERVICE_TYPES',
    'MEETINGS_EVENTS',
    'TRANSPORTATION_TYPES',
    'LEISURE_REGIONS',
    'CITIES',
    'STATES',
    'COUNTRIES',
  ];

  const extractFacetsFromShopState = useCallback((shopState: any): NLSSelectedFacetMap => {
    const result: NLSSelectedFacetMap = {};
    for (const key of SHOP_FACET_KEYS) {
      const facetObj = shopState?.[key];
      if (facetObj && typeof facetObj === 'object' && Object.keys(facetObj).length > 0) {
        result[key] = facetObj;
      }
    }
    return result;
  }, []);

  const baseQueryRef = useRef<string>('');
  // Keep base query ref fresh from llmMetadata AND seed the hook's internal ref
  // so that onFacetClick / onApplyFilters fire correctly from the initial TIP result
  // (before the user has triggered any MiniSearchBar search).
  //
  // For single-property responses (availability_calendar_search) there is no NLS
  // query on the current response, so the FE UXL/NLS call must always reuse the NLS
  // query from the last NLS-backed message in the conversation. We resolve it from
  // the store messages at call time so it stays correct across reloads and
  // conversation switches (where the previous ref value would be lost).
  useEffect(() => {
    let q = llmMetadata?.payload?.query ?? llmMetadata?.llmInfo?.queryRewrite ?? '';
    if (!q && isSinglePropertyResponse) {
      q = findLatestBaseQueryFromMessages(chatMessages) ?? '';
    }
    if (q) {
      baseQueryRef.current = q;
      setBaseQuery(q);
    }
  }, [llmMetadata, setBaseQuery, isSinglePropertyResponse, chatMessages]);

  useEffect(() => {
    const handleFilterSelection = (event: Event) => {
      const shopState = (event as CustomEvent).detail;
      const facets = extractFacetsFromShopState(shopState);
      pendingFacetsRef.current = facets;
      // Fire facets-only NLS refresh to get updated bucket counts
      onFacetClick(facets).then(nlsFacets => {
        // nlsFacets is the array returned by the NLS filter query.
        // Wrap it in { facets: [...] } to match the shape FiltersComponent reads:
        //   filterUpdateData?.facets  (from filtersComponent/index.tsx)
        if (nlsFacets) {
          setAiFilterUpdateData({ facets: nlsFacets });
        }
      });
    };

    const handleApply = (event: Event) => {
      // Capture the user's current selections so they can be restored while
      // this conversational result context remains active.
      if (Object.keys(pendingFacetsRef.current).length > 0) {
        setAiAppliedFacets({ ...pendingFacetsRef.current });
        // Sync applied facets to conversation filter state for /validate and Search All Properties
        if (currentChatId && setConversationFilterState) {
          const filterState = convertFacetsToFilterState(pendingFacetsRef.current);
          setConversationFilterState(currentChatId, filterState);
        }
        // Mark this as a user-applied filter search so the viewData effect
        // doesn't overwrite the filter state when the search result comes back
        userAppliedFilterSearchRef.current = true;
      } else {
        // Filters were cleared — reset applied facets so no stale selections persist.
        setAiAppliedFacets(undefined);
        // Clear filter state in conversation store
        if (currentChatId && setConversationFilterState) {
          setConversationFilterState(currentChatId, {
            brands: [],
            amenities: [],
            priceRange: {},
            pointsRange: {},
            hotelTypes: [],
            activities: [],
            transportationTypes: [],
            events: [],
            distance: undefined,
            states: [],
            cities: [],
          });
        }
      }
      // User is now explicitly in control of filters — NLP pre-selection
      // is derived from viewData (not state) so no setter is needed here.
      // Extract includeUnavailableProperties from the event detail if available
      const eventDetail = (event as CustomEvent).detail;
      const includeUnavailableProperties =
        typeof eventDetail === 'object' && eventDetail !== null ? eventDetail.includeUnavailableProperties : undefined;
      onApplyFilters(pendingFacetsRef.current, { includeUnavailableProperties });
    };

    document.addEventListener('updateShopPageFilterModalValues', handleFilterSelection);
    document.addEventListener('callSessionData', handleApply);
    return () => {
      document.removeEventListener('updateShopPageFilterModalValues', handleFilterSelection);
      document.removeEventListener('callSessionData', handleApply);
    };
  }, [onFacetClick, onApplyFilters, extractFacetsFromShopState, currentChatId, setConversationFilterState]);

  const handleNLSSearch = useCallback(
    async (params: NLSSearchTriggerParams) => {
      if (!params.baseQueryRewrite) {
        return;
      }

      // Any user-triggered search via MiniSearchBar locks in the current pending
      // facets as the active user-applied state. NLP pre-selection is derived from
      // viewData synchronously, so no setter is needed here.
      if (Object.keys(pendingFacetsRef.current).length > 0) {
        setAiAppliedFacets({ ...pendingFacetsRef.current });
      }

      // Each onChange handler updates the store AND triggers executeSearch internally.
      // Do NOT call executeSearch again after these — that would double-fire the search.
      switch (params.triggerType) {
        case 'dates':
          if (params.dates) {
            await onDatesChange({
              checkin: params.dates.checkin,
              checkout: params.dates.checkout,
              flexible: params.dates.flexible,
              numberOfNights: params.dates.numberOfNights,
            });
          } else {
            await executeSearch(params.baseQueryRewrite);
          }
          break;

        case 'rooms':
          if (params.roomsAndGuests) {
            await onRoomsChange({
              roomsCount: params.roomsAndGuests.roomsCount,
              adultsCount: params.roomsAndGuests.adultsCount,
              childrenCount: params.roomsAndGuests.childrenCount,
              childrenAges: params.roomsAndGuests.childrenAges,
            });
          } else {
            await executeSearch(params.baseQueryRewrite);
          }
          break;

        case 'rates':
          if (params.specialRates) {
            await onRatesChange({
              clusterCode: params.specialRates.clusterCode,
              corporateCode: params.specialRates.corporateCode,
              groupCode: params.specialRates.groupCode,
            });
          } else {
            await executeSearch(params.baseQueryRewrite);
          }
          break;

        case 'filters':
          // MiniSearchBar "Apply" always sends triggerType: 'filters'.
          // The store is already updated by NGSpecialRatesAndPoints / NGDatepicker etc.
          // before onNLSSearch fires, so we just run the search.
          await executeSearch(params.baseQueryRewrite);
          break;

        default:
          break;
      }
    },
    [executeSearch, onDatesChange, onRoomsChange, onRatesChange, setAiAppliedFacets]
  );

  const isSearchLoading = isMiniSearchLoading || isNLSLoading;
  // Show MiniSearchBar for all cases except static map
  const showSearchBar = !isStaticMap;
  const showFloatingLoader = !isStaticMap && isSearchLoading && !isMapView;

  // Read live rate state from the store. Written by NGSpecialRatesAndPoints on
  // user interaction AND by buildStoreUpdatesFromAmpRequest after each NLS response.
  // Using the store ensures SearchResultsComponent sees the correct rateRequestTypes
  // immediately when the user changes rates in the MiniSearchBar.
  const storeSpecialRates = useSearchFormStore((s: any) => s[SPECIAL_RATES]);
  const storeUsePoints = useSearchFormStore((s: any) => s[USE_POINTS]);

  const searchQueryData = useMemo(
    () => buildSearchQueryDataFromStore(storeSpecialRates, storeUsePoints),
    [storeSpecialRates, storeUsePoints]
  );


  const miniSearchBarProps = {
    model: miniSearchBarModel,
    isLoading: isSearchLoading,
    llmMetadata,
    currentQuery: baseQueryRef.current || undefined,
    onSearch: (_params: any) => { },
    onNLSSearch: handleNLSSearch
  };
  // Key SearchResultsComponent on the current view state so it remounts when the
  // user navigates to a new view (new query), while staying stable across view-mode
  // toggles that use updateViewData instead of navigateToView.
  const searchResultsContextKey = currentViewStateId ?? 'initial';

  return (
    <ResultsRelativeWrapper $isMapView={isMapView}>
      {/* ── List view: pill bar sits above the cards in normal flow ── */}
      {showSearchBar && !isMapView && (
        <SearchBarStrip $isMapView={false}>
          <PillWrapper>
            <MiniSearchBar {...miniSearchBarProps} />
          </PillWrapper>
        </SearchBarStrip>
      )}

      {/* ── Map view: pill bar floats over the map (desktop) ── */}
      {showSearchBar && isMapView && (
        <SearchBarStrip $isMapView={true}>
          <PillWrapper>
            <MiniSearchBar {...miniSearchBarProps} />
          </PillWrapper>
        </SearchBarStrip>
      )}

      {/* ── Floating animated-dots pill — shown while loading over list view ── */}
      {showFloatingLoader && (
        <FloatingLoadingPill aria-label="Loading results" role="status">
          <Dot $delay={0} />
          <Dot $delay={160} />
          <Dot $delay={320} />
        </FloatingLoadingPill>
      )}

      <OSearchResultsComponent
        key={searchResultsContextKey}
        miSearchResults={{}}
        searchResults={{}}
        isAuthorMode={false}
        AiSearchResults={viewData?.rawData ? viewData : undefined}
        messageUiData={uiData}
        model={searchResultMock}
        onOpenHotelPanel={onOpenHotelPanel}
        onPropertyMediaLoaded={onPropertyMediaLoaded}
        isStaticMap={isStaticMap}
        isMapLoading={isMapLoading}
        searchQueryData={searchQueryData}
        aiFilterUpdateData={aiFilterUpdateData}
        aiPreSelectedFacets={aiAppliedFacets !== undefined ? aiAppliedFacets : nlpPreSelectedFacets}
        isMapView={isMapView}
        chatIsFlexDate={viewData?.isFlexDate === true}
        isSinglePropertyResponse={isSinglePropertyResponse}
        GOOGLE_MAP_API_KEY=""
      />
    </ResultsRelativeWrapper>
  );
};
